import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { EM_DASH } from 'libs.constants';
import { FirebaseAppContext } from 'libs.firebase_react';
import { LocalizationOrigin, } from 'libs.localization.common';
import { useLocalization } from 'libs.localization.react';
import { useLocalizeMessage, ModalWindow, RadioGroup, RadioGroupOption, Spinner, Table, Text, Badge, DateTimeFormat, useToastNotification, } from 'libs.nucleus';
import { TranslationWorkbenchContext } from '../../contexts';
import { useLibraryDetails, useSetStudyAssetVersion } from '../../hooks';
import { toSentenceCase } from '../../utils/string_utils';
import { AssetVersion } from '../asset_version/asset_version.component';
const cloneStudyWithL10nMapResourceId = (study, l10nMapResourceId) => {
    const nextStudy = Object.assign(Object.create(Object.getPrototypeOf(study)), study);
    nextStudy.l10nMapResourceId = l10nMapResourceId;
    return nextStudy;
};
export const AssetVersionSelectionModal = ({ onModalClose, data, asset, displayAssetName, }) => {
    const t = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { studyId, assetId } = useParams();
    const { data: library } = useLibraryDetails(assetId);
    const { logEvent } = useContext(FirebaseAppContext);
    const { study, updateStudy } = useContext(TranslationWorkbenchContext);
    const [versions, setVersions] = useState([]);
    const { mutate: setStudyAssetVersion, isPending: isSettingStudyL10nMap } = useSetStudyAssetVersion();
    const { localization } = useLocalization(asset);
    const currentStatus = data.currentVersionStatus?.status || data.status;
    const resource = library?.data.data.data.activity || library?.data.data.data;
    useEffect(() => {
        if (localization && versions.length === 0) {
            (async () => {
                const _history = await localization?.getLocaleHistory(data.locale);
                const sortedHistory = _history?.sort((a, b) => (a?.version ?? 0) - (b?.version ?? 0)); // sorted numeric ascending
                if (_history) {
                    setVersions(sortedHistory?.map((h) => ({
                        version: Number(h.version),
                        status: h.status,
                        origin: h.localization_origin || LocalizationOrigin.USER,
                        lastUpdated: h.last_updated ? h.last_updated.toDate().toISOString() : '',
                    })) || []);
                }
            })();
        }
    }, [localization]);
    const [selectedVersion, setSelectedVersion] = useState(`${data.currentVersionStatus?.version || data.version}-${currentStatus}`);
    const onChangeClick = useCallback(async () => {
        logEvent('translations_asset_version_modal_submit', {
            studyId,
            assetId,
            selectedVersion,
        });
        if (selectedVersion && asset && studyId) {
            // The backend (`POST /api/v2/translation/study/asset_version`) publishes the l10n map
            // and updates the study config; the browser only refreshes local study state.
            const assetLocaleVersion = {
                id: localization?.getId() || '',
                locale: data.locale,
                version: Number(selectedVersion.split('-')[0]),
            };
            setStudyAssetVersion({ studyId: parseInt(studyId, 10), assetLocaleVersionSet: [assetLocaleVersion] }, {
                onSuccess: (response) => {
                    if (response?.data?.l10nMapResourceId) {
                        updateStudy?.(cloneStudyWithL10nMapResourceId(study, response.data.l10nMapResourceId));
                    }
                    onModalClose();
                },
                onError: (error) => {
                    // Previously a silent failure: the modal stayed open with no feedback.
                    console.error('Failed to set the translation version:', error);
                    addNotification({
                        title: t('Failed to change the translation version. Please try again.'),
                        type: 'error',
                    });
                },
            });
        }
    }, [selectedVersion, data]);
    const onSelectedVersion = useCallback((version) => {
        logEvent('translations_asset_select_version', {
            studyId,
            assetId,
            version,
        });
        setSelectedVersion(version);
    }, []);
    const onModalCloseClick = useCallback(() => {
        logEvent('translations_asset_version_modal_cancel', {
            studyId,
            assetId,
        });
        onModalClose();
    }, [onModalClose]);
    const doneButton = {
        label: t('Change version'),
        onClick: onChangeClick,
        disabled: selectedVersion === `${data.currentVersionStatus?.version || data.version}-${currentStatus}`,
        content: isSettingStudyL10nMap ? (_jsx("div", { className: 'py-1', children: _jsx(Spinner, { size: 'sm', variant: 'alt' }) })) : undefined,
    };
    const closeButton = {
        label: t('Cancel'),
        onClick: onModalCloseClick,
        disabled: isSettingStudyL10nMap,
    };
    const columns = useMemo(() => {
        return [
            {
                header: t('Version'),
                cell: ({ row }) => {
                    return (_jsxs("div", { className: 'flex w-full items-center gap-4', children: [_jsx(RadioGroupOption, { option: {
                                    value: `${row.original.version}-${row.original.status}`,
                                    label: '',
                                } }), _jsx(AssetVersion, { currentVersion: row.original }), _jsx("span", { className: 'flex-1' })] }));
                },
            },
            {
                header: t('Source'),
                cell: ({ row }) => _jsx("div", { className: 'min-w-32', children: t(row.original.origin) }),
            },
            {
                header: t('Date updated'),
                cell: ({ row }) => (_jsx("div", { className: 'min-w-32', children: row.original.lastUpdated ? _jsx(DateTimeFormat, { date: row.original.lastUpdated }) : EM_DASH })),
            },
            {
                header: ' ',
                cell: ({ row }) => (_jsxs("div", { className: 'flex gap-1 justify-end', children: [row.original.version === data.currentVersionStatus?.version && _jsx(Badge, { label: t('Current') }), row.original.version === data.latestVersionStatus?.version && _jsx(Badge, { label: t('Latest') })] })),
            },
        ];
    }, [versions]);
    return (_jsx(ModalWindow, { isOpen: true, title: t('View or change version'), position: 'top', closeWindow: onModalClose, footerSecondaryActionButtons: [closeButton], footerPrimaryActionButton: doneButton, width: 'full', children: _jsxs("div", { children: [_jsxs("div", { "data-testid": 'asset_version_selection_modal_description', children: [_jsx(Text, { as: 'span', children: t('Here are the available versions for ') }), _jsx(Text, { as: 'span', className: 'font-medium', children: resource?.full_name || displayAssetName }), _jsxs(Text, { as: 'span', children: [" ", t('locale'), " "] }), _jsx(Text, { as: 'span', className: 'font-medium', children: data.locale }), _jsx(Text, { as: 'span', children: t('. If you change version, the operation is reversible.') }), _jsx(Text, { as: 'span', children: t(' Your current version is ') }), _jsxs(Text, { as: 'span', className: 'font-medium', children: ["v", data.currentVersionStatus?.version || data.version || 1, ' ', toSentenceCase(currentStatus)] })] }), _jsx("hr", { className: 'mt-6' }), versions && (_jsx(RadioGroup, { value: selectedVersion, onChange: onSelectedVersion, "data-testid": 'asset_version_selection_modal_radio', children: _jsx(Table, { data: versions, columns: columns, labelNoResults: t('No versions available') }) }))] }) }));
};
